import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import AppleCarousel from './AppleCarousel';

function PopularFoods({ culture, userId, onSelect }) {
  const [foods, setFoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingFoodId, setCheckingFoodId] = useState(null);

  const loadPopularFoods = useCallback(async () => {
    setLoading(true);
    try {
      // Get all foods for the culture
      const results = await api.searchFoods('', culture);
      // Get first 6 foods as popular, prioritize SAFE foods
      const safeFoods = results.filter(f => f.safetyStatus === 'SAFE').slice(0, 4);
      const otherFoods = results.filter(f => f.safetyStatus !== 'SAFE').slice(0, 2);
      setFoods([...safeFoods, ...otherFoods].slice(0, 6));
    } catch (error) {
      console.error('Error loading foods:', error);
      setFoods([]);
    } finally {
      setLoading(false);
    }
  }, [culture]);

  useEffect(() => {
    loadPopularFoods();
  }, [culture, loadPopularFoods]);

  const handleFoodClick = async (food) => {
    setCheckingFoodId(food.id || food.name);
    try {
      const response = await api.checkFoodSafety(food.name, culture, 'en', userId);
      onSelect(response);
      if (userId) {
        window.dispatchEvent(new CustomEvent('food-history-updated', { detail: { userId: String(userId) } }));
      }
    } catch (error) {
      console.error('Error checking food:', error);
      onSelect({
        foodName: food.name,
        safetyStatus: food.safetyStatus,
        message: food.safetyStatus === 'SAFE' 
          ? 'Safe to eat during pregnancy' 
          : food.safetyStatus === 'UNSAFE'
          ? 'Not recommended during pregnancy'
          : 'Eat with caution',
        reason: food.reason,
        alternative: food.alternative,
      });
    } finally {
      setCheckingFoodId(null);
    }
  };

  const getSafetyClass = (status) => {
    return status?.toLowerCase() || 'unknown';
  };

  const getSafetyLabel = (status) => {
    switch (status) {
      case 'SAFE':
        return 'Safe to eat during pregnancy';
      case 'UNSAFE':
        return 'Not recommended during pregnancy';
      case 'CAUTION':
        return 'Eat with caution';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="popular-foods">
        <h2>Popular Foods</h2>
        <p>Loading...</p>
      </div>
    );
  }

  if (foods.length === 0) {
    return null;
  }

  return (
    <div className="popular-foods">
      <h2>Popular Foods</h2>
      <p className="popular-foods-subtitle">
        Click on a food to check its safety
      </p>
      <AppleCarousel>
        {foods.map((food) => (
          <div
            key={food.id}
            className={`popular-food-card ${getSafetyClass(food.safetyStatus)}`}
            onClick={() => handleFoodClick(food)}
          >
            <div className="food-name">{food.name}</div>
            <div className={`safety-badge ${getSafetyClass(food.safetyStatus)}`}>
              {getSafetyLabel(food.safetyStatus)}
            </div>
            {food.reason && (
              <div className="food-reason-preview">
                {food.reason.substring(0, 60)}...
              </div>
            )}
            {checkingFoodId === (food.id || food.name) && (
              <div className="food-logging-indicator">
                Logging...
              </div>
            )}
          </div>
        ))}
      </AppleCarousel>
    </div>
  );
}

export default PopularFoods;

